import type { WorkflowOutput, AgentConfigOutput } from "@webuild-ai/admin/models/components";

import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  Listbox,
  ListboxItem,
  ListboxSection,
  Chip,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

export interface CatalogueAgent {
  agentId: string;
  workflowId: string;
  workflowName: string;
  config: AgentConfigOutput;
}

interface PlaygroundAgentCatalogueProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (agent: CatalogueAgent) => void;
}

const SYSTEM_WORKFLOW_IDS = new Set(["chat_agent", "workflow_generator"]);

const PlaygroundAgentCatalogue: React.FC<PlaygroundAgentCatalogueProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();
  const [workflows, setWorkflows] = useState<WorkflowOutput[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen && adminClient) {
      const fetchWorkflows = async () => {
        setIsLoading(true);
        try {
          const result = await adminClient.workflows.listWorkflows({});

          setWorkflows(result);
        } catch (error) {
          logger.error("Failed to fetch workflows for agent catalogue", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchWorkflows();
    }
  }, [isOpen, adminClient]);

  // Flatten all agents from all non-system workflows into a searchable list
  const catalogueAgents = useMemo(() => {
    const agents: CatalogueAgent[] = [];

    for (const wf of workflows) {
      if (SYSTEM_WORKFLOW_IDS.has(wf.id)) continue;
      if (!wf.agents) continue;

      for (const [agentId, config] of Object.entries(wf.agents)) {
        agents.push({
          agentId,
          workflowId: wf.id,
          workflowName: wf.name,
          config,
        });
      }
    }

    return agents;
  }, [workflows]);

  // Filter by search query across name, description, and system_message
  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return catalogueAgents;

    const q = searchQuery.toLowerCase();

    return catalogueAgents.filter(
      (agent) =>
        agent.agentId.toLowerCase().includes(q) ||
        (agent.config.description || "").toLowerCase().includes(q) ||
        ((agent.config.parameters?.system_message as string) || "").toLowerCase().includes(q),
    );
  }, [catalogueAgents, searchQuery]);

  // Group filtered agents by workflow
  const groupedByWorkflow = useMemo(() => {
    const groups = new Map<string, { workflowName: string; agents: CatalogueAgent[] }>();

    for (const agent of filteredAgents) {
      const existing = groups.get(agent.workflowId);

      if (existing) {
        existing.agents.push(agent);
      } else {
        groups.set(agent.workflowId, {
          workflowName: agent.workflowName,
          agents: [agent],
        });
      }
    }

    return groups;
  }, [filteredAgents]);

  const handleSelect = (agent: CatalogueAgent) => {
    onImport(agent);
    onClose();
    setSearchQuery("");
  };

  return (
    <Modal isOpen={isOpen} size="lg" onOpenChange={(open) => !open && onClose()}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3>{t("Agent Catalogue")}</h3>
          <p className="text-xs text-default-500 font-normal">
            {t("Browse and import agents from existing workflows")}
          </p>
        </ModalHeader>
        <ModalBody className="pb-6">
          <Input
            placeholder={t("Search by name, description, or system message...")}
            size="sm"
            startContent={
              <Icon className="w-4 h-4 text-default-400" icon="solar:magnifer-linear" />
            }
            value={searchQuery}
            onValueChange={setSearchQuery}
          />

          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-8 text-default-400">
                <Icon
                  className="w-6 h-6 mx-auto mb-2 animate-spin"
                  icon="solar:refresh-circle-linear"
                />
                <p className="text-sm">{t("Loading agents...")}</p>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="text-center py-8 text-default-400">
                <Icon className="w-6 h-6 mx-auto mb-2" icon="solar:user-hand-up-outline" />
                <p className="text-sm">{t("No agents found")}</p>
              </div>
            ) : (
              <Listbox
                aria-label={t("Agent catalogue")}
                onAction={(key) => {
                  const agent = filteredAgents.find((a) => `${a.workflowId}::${a.agentId}` === key);

                  if (agent) handleSelect(agent);
                }}
              >
                {Array.from(groupedByWorkflow.entries()).map(
                  ([workflowId, { workflowName, agents }]) => (
                    <ListboxSection key={workflowId} title={workflowName}>
                      {agents.map((agent) => (
                        <ListboxItem
                          key={`${workflowId}::${agent.agentId}`}
                          startContent={
                            <Icon
                              className="w-5 h-5 text-blue-500"
                              icon="solar:user-hand-up-outline"
                            />
                          }
                          textValue={agent.agentId}
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{agent.agentId}</span>
                              <Chip size="sm" variant="flat">
                                {agent.config.type}
                              </Chip>
                            </div>
                            {agent.config.description && (
                              <span className="text-xs text-default-400 line-clamp-1">
                                {agent.config.description}
                              </span>
                            )}
                          </div>
                        </ListboxItem>
                      ))}
                    </ListboxSection>
                  ),
                )}
              </Listbox>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PlaygroundAgentCatalogue;
